<template>
  <v-container
    id="dashboard-user"
    fluid
    tag="section"
    class="dashboard-container"
    style="padding: 24px 96px;"
  >
    <v-row class="mb-2 ma-n2" align="center">
      <v-col cols="12" sm="6" md="2" class="pa-2">
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filterStartDate"
              label="Data Início"
              prepend-inner-icon="mdi-calendar"
              readonly
              dense
              outlined
              hide-details
              clearable
              class="elevment-input"
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click:clear="filterStartDate = null"
            />
          </template>
          <v-date-picker
            v-model="filterStartDate"
            @input="startDateMenu = false"
            locale="pt-BR"
            no-title
            color="primary"
          />
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="pa-2">
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filterEndDate"
              label="Data Fim"
              prepend-inner-icon="mdi-calendar"
              readonly
              dense
              outlined
              hide-details
              clearable
              class="elevment-input"
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click:clear="filterEndDate = null"
            />
          </template>
          <v-date-picker
            v-model="filterEndDate"
            :min="filterStartDate"
            @input="endDateMenu = false"
            locale="pt-BR"
            no-title
            color="primary"
          />
        </v-menu>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="pa-2">
        <v-select
          v-model="quickDateFilter"
          :items="quickDateOptions"
          label="Últimos dias"
          prepend-inner-icon="mdi-calendar-range"
          dense
          outlined
          hide-details
          clearable
          class="elevment-input"
          color="primary"
          @change="applyQuickFilter"
        />
      </v-col>

      <v-col cols="12" sm="6" md="auto" class="pa-2 d-flex" style="gap: 12px">
        <v-btn
          color="primary"
          height="40"
          depressed
          class="px-5 font-weight-bold"
          style="border-radius: 8px; color: var(--text-on-primary);"
          @click="applyDateFilter"
        >
          Aplicar
        </v-btn>
        <v-btn
          height="40"
          text
          class="text--secondary font-weight-bold"
          style="border-radius: 8px;"
          @click="clearDateFilter"
        >
          Limpar
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="ma-n2 top-cards-row">
      <v-col cols="12" sm="6" md="3" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 flex-grow-1 profile-card">
          <v-card-text v-if="loading.profile" class="d-flex align-center justify-center" style="height: 100%">
            <v-progress-circular indeterminate color="primary" />
          </v-card-text>
          <v-card-text v-else class="pa-4 d-flex flex-column align-center justify-center text-center" style="height: 100%">
            <v-avatar size="64" class="mb-3" color="lime accent-3">
              <v-img v-if="user.avatarUrl" :src="user.avatarUrl" :alt="user.name" />
              <span v-else class="black--text font-weight-bold text-h6">{{ getInitials(user.name) }}</span>
            </v-avatar>
            
            <div class="text-subtitle-1 font-weight-bold text--primary" style="line-height: 1.2;">
              {{ user.name }}
            </div>
            <div class="text-caption text--secondary mb-2">
              {{ user.jobRoleName || 'Cargo não informado' }}<br>
              {{ user.departmentName || 'Departamento' }}
            </div>
            
            <v-rating
              :value="4"
              readonly
              dense
              color="amber"
              background-color="grey darken-2"
              size="14"
              class="mb-3"
            />
            
            <v-divider class="theme-divider w-100 mb-2" style="width: 100%" />
            
            <div class="text-caption text--secondary">
              Gestor: <span class="font-weight-medium text--primary">{{ user.managerName || 'Não atribuído' }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="text-center pa-4 d-flex flex-column align-center justify-center" style="height: 100%">
            <div class="d-flex align-center mb-3">
              <v-icon color="primary" small class="mr-2">mdi-ribbon</v-icon>
              <span class="text-subtitle-2 font-weight-bold text--primary">Score Geral</span>
            </div>
            
            <v-progress-circular
              :value="scores.general"
              :size="100"
              :width="10"
              color="primary"
            >
              <div class="d-flex flex-column align-center">
                <span class="text-h4 font-weight-bold text--primary" style="line-height: 1">{{ scores.general }}</span>
                <span class="text-caption text--secondary">de 100</span>
              </div>
            </v-progress-circular>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="text-center pa-4 d-flex flex-column justify-center align-center" style="height: 100%">
            <div class="d-flex align-center mb-2">
              <v-icon color="success" small class="mr-2">mdi-check-decagram</v-icon>
              <span class="text-subtitle-2 font-weight-bold text--primary">Qualidade</span>
            </div>
            
            <div class="text-h3 font-weight-bold success--text mb-4">{{ scores.quality }}<span class="text-h6">%</span></div>
            
            <v-progress-linear
              :value="scores.quality"
              color="success"
              height="6"
              rounded
              class="mb-2 w-100"
              background-color="rgba(128,128,128,0.15)"
            />
            <div class="text-caption text--secondary">Qualidade das entregas</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 top-card flex-grow-1">
          <v-card-text class="text-center pa-4 d-flex flex-column justify-center align-center" style="height: 100%">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" small class="mr-2">mdi-chart-timeline-variant</v-icon>
              <span class="text-subtitle-2 font-weight-bold text--primary">Consistência</span>
            </div>
            
            <div class="text-h3 font-weight-bold primary--text mb-4">{{ scores.consistency }}<span class="text-h6">%</span></div>
            
            <v-progress-linear
              :value="scores.consistency"
              color="primary"
              height="6"
              rounded
              class="mb-2 w-100"
              background-color="rgba(128,128,128,0.15)"
            />
            <div class="text-caption text--secondary">Regularidade de performance</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="ma-n2 mt-2">
      <v-col cols="12" sm="6" md="3" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 summary-card flex-grow-1" hover @click="$router.push('/pdis')">
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <div class="icon-box-tonal primary--text mr-3 flex-shrink-0">
                <v-icon color="primary">mdi-target</v-icon>
              </div>
              <div class="summary-text-wrap flex-grow-1">
                <div class="text-subtitle-2 font-weight-bold text--primary">Meu Desenvolvimento (PDI)</div>
                <div class="text-caption text--secondary mt-1"><span class="primary--text font-weight-bold">{{ summaryPdi }}</span> metas ativas</div>
              </div>
              <v-icon color="grey" small>mdi-chevron-right</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 summary-card flex-grow-1" hover @click="$router.push('/campaigns')">
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <div class="icon-box-tonal warning--text mr-3 flex-shrink-0">
                <v-icon color="warning">mdi-bullhorn-outline</v-icon>
              </div>
              <div class="summary-text-wrap flex-grow-1">
                <div class="text-subtitle-2 font-weight-bold text--primary">Minhas Campanhas</div>
                <div class="text-caption text--secondary mt-1"><span class="warning--text font-weight-bold">{{ summaryCampaigns }}</span> pendentes</div>
              </div>
              <v-icon color="grey" small>mdi-chevron-right</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 summary-card flex-grow-1" hover @click="$router.push('/feedbacks')">
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <div class="icon-box-tonal success--text mr-3 flex-shrink-0">
                <v-icon color="success">mdi-message-outline</v-icon>
              </div>
              <div class="summary-text-wrap flex-grow-1">
                <div class="text-subtitle-2 font-weight-bold text--primary">Meus Feedbacks</div>
                <div class="text-caption text--secondary mt-1"><span class="success--text font-weight-bold">{{ summaryFeedbacks }}</span> recebidos</div>
              </div>
              <v-icon color="grey" small>mdi-chevron-right</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3" class="pa-2 d-flex">
        <v-card class="elevment-card elevation-0 summary-card flex-grow-1" hover @click="$router.push('/one-on-ones')">
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <div class="icon-box-tonal deep-purple--text mr-3 flex-shrink-0">
                <v-icon color="deep-purple accent-2">mdi-calendar-month-outline</v-icon>
              </div>
              <div class="summary-text-wrap flex-grow-1">
                <div class="text-subtitle-2 font-weight-bold text--primary">Próximas 1:1</div>
                <div class="text-caption text--secondary mt-1"><span class="deep-purple--text text--accent-2 font-weight-bold">{{ summaryOneOnOnes }}</span> reunião esta semana</div>
              </div>
              <v-icon color="grey" small>mdi-chevron-right</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="ma-n2 mt-2">
      <v-col cols="12" md="6" class="pa-2">
        <v-card class="elevment-card elevation-0 detail-card">
          <v-card-title class="d-flex justify-space-between align-center px-6 pt-5 pb-3">
            <div class="d-flex align-center">
              <v-icon color="primary" small class="mr-2">mdi-message-outline</v-icon>
              <span class="text-subtitle-1 font-weight-bold text--primary">Últimos feedbacks recebidos</span>
            </div>
            <a class="text-caption font-weight-bold primary--text text-decoration-none" @click="$router.push('/feedbacks')">Ver todos</a>
          </v-card-title>
          <v-divider class="theme-divider" />
          <v-card-text class="px-6 py-4">
            <div v-if="loading.feedbacks" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <div v-else-if="feedbacks.length === 0" class="text-center py-8 text--secondary">
              <v-icon x-large color="grey">mdi-message-outline</v-icon>
              <div class="text-subtitle-1 mt-3">Nenhum feedback recebido</div>
            </div>

            <div v-else>
              <div v-for="(feedback, idx) in feedbacks" :key="feedback.id">
                <div class="d-flex align-start py-4">
                  <v-avatar size="36" color="grey darken-3" class="mr-3 flex-shrink-0">
                    <span class="white--text text-caption font-weight-bold">{{ feedback.initials }}</span>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="d-flex align-center">
                        <span class="font-weight-bold text-body-2 text--primary mr-2">{{ feedback.from }}</span>
                        <span :class="`${feedback.color}--text text-caption font-weight-medium d-flex align-center`">
                          <v-icon x-small :color="feedback.color" class="mr-1">{{ getFeedbackIcon(feedback.type) }}</v-icon>
                          {{ getFeedbackTypeLabel(feedback.type) }}
                        </span>
                      </div>
                      <span class="text-caption text--secondary">{{ feedback.date }}</span>
                    </div>
                    <div class="text-body-2 text--secondary" style="line-height: 1.5">
                      {{ feedback.details || feedback.message }}
                    </div>
                  </div>
                </div>
                <v-divider v-if="idx < feedbacks.length - 1" class="theme-divider" />
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6" class="pa-2">
        <v-card class="elevment-card elevation-0 detail-card">
          <v-card-title class="d-flex justify-space-between align-center px-6 pt-5 pb-3">
            <div class="d-flex align-center">
              <v-icon color="primary" small class="mr-2">mdi-target</v-icon>
              <span class="text-subtitle-1 font-weight-bold text--primary">Metas do PDI</span>
            </div>
            <a class="text-caption font-weight-bold primary--text text-decoration-none" @click="$router.push('/pdis')">Ver PDI</a>
          </v-card-title>
          <v-divider class="theme-divider" />
          <v-card-text class="px-6 py-4">
            <div v-if="loading.goals" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <div v-else-if="pdi.pdis.length === 0" class="text-center py-8 text--secondary">
              <v-icon x-large color="grey">mdi-target</v-icon>
              <div class="text-subtitle-1 mt-3">Nenhuma meta ativa</div>
            </div>

            <div v-else>
              <div v-for="(item, idx) in pdi.pdis" :key="item.id">
                <div class="py-4">
                  <div class="d-flex justify-space-between align-center mb-3">
                    <span class="font-weight-bold text-body-2 text--primary">{{ item.title }}</span>
                    <span class="text-caption text--secondary">Prazo {{ formatDate(item.endDate) || item.deadline }}</span>
                  </div>

                  <div class="d-flex align-center">
                    <v-progress-linear
                      :value="item.progress || 0"
                      color="primary"
                      height="6"
                      rounded
                      class="flex-grow-1 mr-3"
                      background-color="rgba(128,128,128,0.15)"
                    />
                    <span class="text-caption font-weight-medium text--secondary" style="min-width: 32px; text-align: right;">
                      {{ Math.round(item.progress || 0) }}%
                    </span>
                  </div>
                </div>
                <v-divider v-if="idx < pdi.pdis.length - 1" class="theme-divider" />
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// --- 1. RESTAURADOS IMPORTS REAIS ---
import UsersService from '@/services/users.service'
import PdisService from '@/services/pdis.service'

export default {
  name: 'DashboardNew',

  // --- 2. data() LIMPO, ESPERANDO DADOS DA API ---
  data: () => ({
    filterStartDate: null,
    filterEndDate: null,
    startDateMenu: false,
    endDateMenu: false,
    quickDateFilter: null,
    quickDateOptions: [
      { text: 'Últimos 7 dias', value: '7' },
      { text: 'Últimos 15 dias', value: '15' },
      { text: 'Últimos 30 dias', value: '30' },
      { text: 'Últimos 60 dias', value: '60' },
      { text: 'Últimos 90 dias', value: '90' },
      { text: 'Último ano', value: '365' },
    ],

    // Inicializado vazio
    user: {
      name: '',
      jobRoleName: null,
      departmentName: null,
      managerName: null,
      avatarUrl: null,
    },

    // Scores zerados
    scores: {
      general: 0,
      quality: 0,
      consistency: 0,
    },

    // Arrays vazios
    pdi: {
      pdis: [],
      expandedPdis: [],
      nextSteps: [],
    },

    campaigns: [],
    feedbacks: [],
    oneOnOnes: [], // Pode preencher se tiver endpoint

    loading: {
      profile: false,
      goals: false,
      nextSteps: false,
      campaigns: false,
      feedbacks: false,
    },
  }),

  // --- 3. LÓGICA DE COMPUTED REAIS RESTAURADA ---
  computed: {
    summaryPdi() {
      // Conta PDIs ativos baseados no status real vindo do back
      return this.pdi.pdis.filter(p => p.status === 'IN_PROGRESS' || p.status === 'APPROVED').length || 0
    },
    summaryCampaigns() {
      return this.campaigns.length || 0
    },
    summaryFeedbacks() {
      return this.feedbacks.length || 0
    },
    summaryOneOnOnes() {
      // Se não tiver endpoint, pode manter mockado ou esconder a info
      return this.oneOnOnes.length || 0
    },
  },

  // --- 4. LIFECYCLE CHAMANDO FUNÇÕES DE LOAD REAL ---
  mounted() {
    this.loadUserData()
    this.loadScores() // Se os scores vierem separados
    this.loadPdiGoals()
    this.loadCampaigns()
    this.loadFeedbacks()
    // Pode carregar oneOnOnes se houver endpoint: this.loadOneOnOnes()
  },

  // --- 5. LÓGICA DE METHODS REAIS RESTAURADA INTEGRALMENTE ---
  methods: {
    // Lógica de Filtros (Restaurada)
    applyQuickFilter() {
      if (!this.quickDateFilter) {
        this.clearDateFilter()
        return
      }
      const today = new Date()
      const startDate = new Date()
      startDate.setDate(today.getDate() - parseInt(this.quickDateFilter))
      this.filterStartDate = startDate.toISOString().split('T')[0]
      this.filterEndDate = today.toISOString().split('T')[0]
      this.applyDateFilter()
    },
    
    applyDateFilter() {
      // Recarrega dados baseados nas datas
      this.loadPdiGoals()
      this.loadCampaigns()
      this.loadFeedbacks()
    },
    
    clearDateFilter() {
      this.filterStartDate = null
      this.filterEndDate = null
      this.quickDateFilter = null
      this.applyDateFilter()
    },
    
    getDateParams() {
      const params = {}
      if (this.filterStartDate) params.startDate = this.filterStartDate
      if (this.filterEndDate) params.endDate = this.filterEndDate
      return params
    },

    // Chamadas de API (Restauradas)
    async loadUserData() {
      try {
        this.loading.profile = true
        // Chamada real ao serviço
        const response = await UsersService.getProfile()
        const userData = response.data

        // Mapeamento dos dados reais para o objeto local
        this.user = {
          name: userData.name || 'Usuário',
          jobRoleName: userData.jobRole?.name || null,
          departmentName: userData.department?.name || null,
          managerName: userData.manager?.name || null,
          avatarUrl: userData.avatarUrl || null,
        }

        // Se o score vier junto no perfil, atualiza aqui também
        if (userData.performanceScore) {
            this.scores = {
                general: Math.round(userData.performanceScore || 0),
                quality: Math.round(userData.qualityScore || 0),
                consistency: Math.round(userData.consistencyScore || 0),
            }
        }

        // Atualiza localStorage mantendo a lógica anterior
        localStorage.setItem('user', JSON.stringify({
          ...JSON.parse(localStorage.getItem('user') || '{}'),
          ...userData,
          jobRoleName: userData.jobRole?.name,
          departmentName: userData.department?.name,
          managerName: userData.manager?.name,
        }))
      } catch (error) {
        console.error('Erro ao carregar perfil do usuário:', error)
        // Tenta carregar do localStorage em caso de erro (lógica original)
        this.loadUserDataFromLocalStorage()
      } finally {
        this.loading.profile = false
      }
    },

    loadUserDataFromLocalStorage() {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const userData = JSON.parse(userStr)
          this.user = {
            name: userData.name || 'Usuário',
            jobRoleName: userData.jobRoleName,
            departmentName: userData.departmentName,
            managerName: userData.managerName,
            avatarUrl: userData.avatarUrl,
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados do localStorage:', error)
      }
    },

    // Lógica original para scores se vier separado ou do LS
    loadScores() {
      try {
        const userStr = localStorage.getItem('user')
        if (userStr) {
          const userData = JSON.parse(userStr)
          // Só atualiza se não tiver sido atualizado pelo loadUserData
          if (this.scores.general === 0 && userData.performanceScore) {
            this.scores = {
              general: Math.round(userData.performanceScore || 0),
              quality: Math.round(userData.qualityScore || 0),
              consistency: Math.round(userData.consistencyScore || 0),
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar scores:', error)
      }
    },

    async loadPdiGoals() {
      try {
        this.loading.goals = true
        // Chamada real
        const response = await PdisService.list()
        const allPdis = response.data || []
        
        // Filtra e ordena conforme lógica original
        const statusOrder = { IN_PROGRESS: 0, APPROVED: 1, PENDING_APPROVAL: 2, COMPLETED: 3, REJECTED: 4 }
        this.pdi.pdis = allPdis
          .filter(p => p.status !== 'DRAFT')
          .sort((a, b) => (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9))
          // Pega apenas as primeiras para o dashboard se necessário, ex: .slice(0, 3)
      } catch (error) {
        console.error('Erro ao carregar PDIs:', error)
        this.pdi.pdis = []
      } finally {
        this.loading.goals = false
      }
    },

    async loadCampaigns() {
      try {
        this.loading.campaigns = true
        const params = this.getDateParams()
        // Chamada real (assumindo que UsersService tem esse método conforme sua dash anterior)
        const response = await UsersService.getMyCampaigns(params)
        
        let campaigns = response.data || []
        // Filtra apenas ativas conforme lógica original
        this.campaigns = campaigns.filter(c => c.status === 'ACTIVE')
      } catch (error) {
        console.error('Erro ao carregar campanhas:', error)
        this.campaigns = []
      } finally {
        this.loading.campaigns = false
      }
    },

    async loadFeedbacks() {
      try {
        this.loading.feedbacks = true
        // Parâmetros da lógica original
        const params = { 
          limit: 3, // Limitado a 3 para o visual novo
          offset: 0,
          ...this.getDateParams()
        }
        // Chamada real
        const response = await UsersService.getReceivedFeedbacks(params.limit, params.offset, params)
        
        // Mapeia os dados reais tratando as cores dinâmicas
        this.feedbacks = response.data.items.map(feedback => ({
          id: feedback.id,
          from: feedback.fromUser?.name || 'Anônimo',
          details: feedback.content || feedback.message || '',
          date: this.formatDate(feedback.createdAt),
          initials: this.getInitials(feedback.fromUser?.name || 'AN'),
          type: feedback.type, // POSITIVE, IMPROVEMENT, etc.
          color: this.getFeedbackColor(feedback.type), // Função visual mantida
        }))
      } catch (error) {
        console.error('Erro ao carregar feedbacks:', error)
        this.feedbacks = []
      } finally {
        this.loading.feedbacks = false
      }
    },

    // Helpers Visuais e de Formatação (Mantidos e Restaurados)
    formatDate(dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        // Se já vier formatado do back, pode retornar direto. 
        // Se for ISO, precisa formatar. Ex: '10 de Jun'
        return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
      } catch (error) {
        return dateString
      }
    },
    
    getInitials(name) {
      if (!name) return '??'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },

    getFeedbackColor(type) {
      const colors = {
        POSITIVE: 'success',
        CONSTRUCTIVE: 'info',
        NEUTRAL: 'grey',
        IMPROVEMENT: 'info', // Mantido ciano conforme imagem 1
      }
      return colors[type] || 'info'
    },

    getFeedbackIcon(type) {
      const icons = {
        POSITIVE: 'mdi-thumb-up-outline',
        IMPROVEMENT: 'mdi-lightbulb-outline',
      }
      return icons[type] || 'mdi-message-outline'
    },

    getFeedbackTypeLabel(type) {
      const labels = {
        POSITIVE: 'Elogio',
        CONSTRUCTIVE: 'Construtivo',
        NEUTRAL: 'Neutro',
        IMPROVEMENT: 'Melhoria',
      }
      return labels[type] || 'Feedback'
    },
  },
}
</script>

<style scoped lang="scss">
/* Estilos CSS (Mantidos INTEGRALMENTE conforme passo anterior para garantir o visual novo) */
#dashboard-user {
  background-color: var(--bg-main);
  min-height: 100vh;

  ::v-deep {
    .primary {
      background-color: var(--brand-primary) !important;
      border-color: var(--brand-primary) !important;
    }
    .primary--text {
      color: var(--brand-primary) !important;
      caret-color: var(--brand-primary) !important;
    }
    .v-text-field--outlined.v-input--is-focused fieldset,
    .v-select.v-input--is-focused fieldset {
      border-color: var(--brand-primary) !important;
    }
    .v-progress-linear__determinate.primary {
      background-color: var(--brand-primary) !important;
    }
    .v-progress-circular__info {
      color: var(--brand-primary) !important;
    }
  }
}

.dashboard-container {
  max-width: 1440px;
  margin: 0 auto;
}

.elevment-card {
  border-radius: var(--radius-md) !important;
  background-color: var(--bg-card) !important;
  border: 1px solid var(--border-color) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.theme-divider {
  border-color: var(--border-light) !important;
}

.top-cards-row {
  align-items: stretch;
}

.top-card, .profile-card {
  min-height: 180px; 
}

.icon-box-tonal {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  position: relative;
  background-color: transparent;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    background-color: currentColor; 
    opacity: 0.15; 
  }
}

.icon-box-tonal .v-icon {
  opacity: 1 !important;
}

[data-theme="dark"] .icon-box-tonal::before {
  opacity: 0.20;
}

.summary-card {
  cursor: pointer;
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md) !important;
    border-color: var(--brand-primary) !important;
  }
}

.summary-text-wrap {
  min-width: 0;
  overflow: hidden;
}

.summary-text-wrap > div {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-card {
  height: 420px; 
  display: flex;
  flex-direction: column;
}

.detail-card ::v-deep .v-card__title {
  flex-shrink: 0;
}

.detail-card ::v-deep .v-card__text {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.detail-card ::v-deep .v-card__text::-webkit-scrollbar {
  width: 6px;
}
</style>